// src/services/crm/etudiantService.js
import api from '../api';

// L'entité s'appelle « Apprenant » dans l'ERP unifié (terme du Lab Manager et
// standard de la formation professionnelle). Les libellés affichés restent
// « Étudiants » : c'est une décision d'interface, pas de modèle.
const BASE = 'apprenants/';

// ══════════════════════════════════════════════════════════
//  MAPPINGS : valeurs françaises (React) ↔ valeurs Django
// ══════════════════════════════════════════════════════════

const PAYS_MAP = {
  'Tunisie':  'tunisie',
  'France':   'france',
  'Algérie':  'algerie',
  'Maroc':    'maroc',
  'Belgique': 'belgique',
  'Canada':   'canada',
  'Autre':    'autre',
};

const STATUT_MAP = {
  'Actif':     'actif',
  'Abandonné': 'abandonne',
};

const GENRE_MAP = {
  'Homme': 'homme',
  'Femme': 'femme',
  'Autre': 'autre',
};

const NIVEAU_ETUDES_MAP = {
  'Lycée':    'lycee',
  'Bac':      'bac',
  'Licence':  'licence',
  'Master':   'master',
  'Doctorat': 'doctorat',
  'Autre':    'autre',
};

const DIPLOME_MAP = {
  'Baccalauréat': 'baccalaureat',
  'Licence':      'licence',
  'Master':       'master',
  'Aucun':        'aucun',
  'Autre':        'autre',
};

const DOCUMENT_TYPE_MAP_INV = {
  'cin':     'CIN',
  'cv':      'CV',
  'contrat': 'Contrat',
  'recu':    'Reçu',
  'rne':     'RNE',
  'autre':   'Autres',
};

// ── Maps inverses (Django → React) ──
const invertMap = (map) =>
  Object.fromEntries(Object.entries(map).map(([k, v]) => [v, k]));

const PAYS_MAP_INV          = invertMap(PAYS_MAP);
const STATUT_MAP_INV        = invertMap(STATUT_MAP);
const GENRE_MAP_INV         = invertMap(GENRE_MAP);
const NIVEAU_ETUDES_MAP_INV = invertMap(NIVEAU_ETUDES_MAP);
const DIPLOME_MAP_INV       = invertMap(DIPLOME_MAP);

// ══════════════════════════════════════════════════════════
//  CONVERTISSEURS
// ══════════════════════════════════════════════════════════

/**
 * Formulaire React → payload Django (pour create / update)
 *
 * CORRECTION : on envoie uniquement les champs définis pour éviter
 * d'écraser des valeurs existantes avec des chaînes vides ou des
 * valeurs par défaut incorrectes lors d'un PATCH partiel.
 * 
 * formation_sessions : { formation_id: [session_id, …] } — plusieurs sessions
 * par formation (un BTS se suit sur plusieurs sessions). Cf. sessionsParFormation.
 */
export const toApiPayload = (fd, formationIds = [], formationSessions = {}) => {
  const payload = {
    nom:                fd.nom?.trim()    || '',
    prenom:             fd.prenom?.trim() || '',
    email:              fd.email?.trim()  || '',
    telephone:          fd.tel?.trim()    || '',
    ville:              fd.ville?.trim()  || '',
    pays:               PAYS_MAP[fd.pays] || 'tunisie',
    notes:              fd.notes          || '',
    mode_paiement:      'espece',
    // En écriture le champ est `formations_suivies_ids` : `formations_suivies`
    // est la projection en lecture (liste d'IDs dérivée des inscriptions).
    formations_suivies_ids: formationIds,
    formation_sessions:     formationSessions,  // { formation_id: [session_id, …] }
  };

  // CORRECTION : n'envoyer le statut que s'il est défini et mappable,
  // pour éviter d'écraser le statut réel avec une valeur par défaut.
  if (fd.statut && STATUT_MAP[fd.statut]) {
    payload.statut = STATUT_MAP[fd.statut];
  }

  return payload;
};

/**
 * Réponse Django → objet React
 *
 * formations_suivies_detail provient désormais du through model
 * EtudiantFormation et contient, par formation :
 *   - dateInscription  (date d'ajout à cette formation spécifique)
 *   - attestation      'Oui' | 'Non'
 *   - dateAttestation  string | ''
 */
export const fromApiResponse = (e) => {
  const statutFr = STATUT_MAP_INV[e.statut] || e.statut;

  return {
    id:     e.id,
    accesPlateforme: e.acces_plateforme || null,   // accès à la plateforme (null : aucun)
    nom:    e.nom,
    prenom: e.prenom,
    email:  e.email,
    tel:    e.telephone,
    ville:  e.ville || '',
    pays:   PAYS_MAP_INV[e.pays] || e.pays || '',

    dateNaissance: e.date_naissance  || '',
    genre:         GENRE_MAP_INV[e.genre]                     || e.genre         || '',
    niveauEtudes:  NIVEAU_ETUDES_MAP_INV[e.niveau_etudes]     || e.niveau_etudes  || '',
    diplomeObtenu: DIPLOME_MAP_INV[e.diplome_obtenu]          || e.diplome_obtenu || '',

    // IDs bruts pour les filtres et le formulaire
    formations: e.formations_suivies || [],

    // Données enrichies par formation (via EtudiantFormation through model)
    formationsDetail: (e.formations_suivies_detail || []).map(f => ({
      id:              f.id,
      label:           f.intitule,
      duree:           f.duree ? `${f.duree}h` : '',
      dateInscription: f.date_inscription_formation || '',
      attestation:     f.attestation ? 'Oui' : 'Non',
      dateAttestation: f.date_attestation || '',
      session_intitule: f.session_intitule || null,
      session_range:    f.session_range    || null,
      session_id:       f.session_id       || null,
      inscriptionId:    f.inscription_id   || null,   // une ligne par inscription
    })),
    
    formationsNoms: e.formations_noms || '',

    // Date d'inscription globale de l'étudiant (conversion prospect → étudiant)
    dateInscription: e.date_inscription || '',
    statut:          statutFr,

    modeFormation: 'Présentiel',
    notes:         e.notes || '',

    documents: (e.documents || []).map(
      d => DOCUMENT_TYPE_MAP_INV[d.type_document] || d.type_document
    ),

    responsableId: e.responsable ? String(e.responsable) : '',
    responsable:   e.responsable_nom || '',
    dateCreation:  e.date_creation ? e.date_creation.slice(0, 10) : '',
  };
};

// ══════════════════════════════════════════════════════════
//  APPELS API
// ══════════════════════════════════════════════════════════

export const getEtudiants = async (params = {}) => {
  const res = await api.get(BASE, { params });
  return res.data.map(fromApiResponse);
};

export const getEtudiant = async (id) => {
  const res = await api.get(`${BASE}${id}/`);
  return fromApiResponse(res.data);
};

export const createEtudiant = async (fd, formationIds = [], formationSessions = {}) => {
  const payload = toApiPayload(fd, formationIds, formationSessions);
  const res     = await api.post(BASE, payload);
  return fromApiResponse(res.data);
};

/** [{ formation_id, session_id }] (SessionSelectionModal) → { formation_id: [session_id, …] } */
export const sessionsParFormation = (formationsWithSessions = []) => {
  const dict = {};
  formationsWithSessions.forEach(({ formation_id, session_id }) => {
    if (!session_id) return;
    const cle = String(formation_id);
    dict[cle] = [...(dict[cle] || []), session_id];
  });
  return dict;
};

/** Sessions seules (fiche détaillée → « Gérer les sessions ») : le reste de la fiche n'est pas renvoyé. */
export const updateSessionsEtudiant = async (id, formationIds, formationsWithSessions) => {
  const res = await api.patch(`${BASE}${id}/`, {
    formations_suivies_ids: formationIds,
    formation_sessions: sessionsParFormation(formationsWithSessions),
  });
  return fromApiResponse(res.data);
};

export const updateEtudiant = async (id, fd, formationIds = [], formationSessions = {}) => {
  const payload = toApiPayload(fd, formationIds, formationSessions);
  const res     = await api.patch(`${BASE}${id}/`, payload);
  return fromApiResponse(res.data);
};

export const deleteEtudiant = async (id) => {
  await api.delete(`${BASE}${id}/`);
};

/**
 * Atteste une formation pour un étudiant.
 *
 * Retourne un objet :
 *   { diplomeCree: false, etudiant: <objet React> }
 *   { diplomeCree: true,  diplomes: [...] }   ← toutes les formations attestées
 *
 * Quand diplomeCree === true, l'étudiant a été supprimé côté backend.
 */
export const attesterFormation = async (etudiantId, formationId, dateAttestation) => {
  const res = await api.post(
    `${BASE}${etudiantId}/formations/${formationId}/attester/`,
    { date_attestation: dateAttestation || null }
  );

  const data = res.data;

  // Maintenant, l'API retourne toujours diplome_cree = true
  // avec un seul diplôme (plus un tableau diplomes)
  return {
    diplomeCree: true,
    diplome: data.diplome,           // L'objet diplôme créé
    etudiantSupprime: data.etudiant_supprime || false,
    etudiant: data.etudiant ? fromApiResponse(data.etudiant) : null,
    message: data.message,
  };
};

export const getNouveauxEtudiants = () => api.get('users/apprenants/nouveaux/');
export const markEtudiantsAsRead = () => api.post('users/apprenants/marquer-lu/');